import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Info, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { api, fmtUSD, usdToCents } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { SecurityBanner } from '@/components/SecurityBanner';
import { ImageUpload } from '@/components/ImageUpload';

const computeLocal = (labor, expenses) => {
  const mo = labor || 0;
  const fa = (expenses || []).reduce((s, e) => s + (e.amount_cents || 0), 0);
  const feeMoIns = Math.round(mo * 0.03);
  const feeMoOps = Math.round(mo * 0.02);
  const feeMoSal = Math.round(mo * 0.03);
  const feeMoMkt = Math.round(mo * 0.01);
  const feeMoSoc = Math.round(mo * 0.01);
  const feeMo = feeMoIns + feeMoOps + feeMoSal + feeMoMkt + feeMoSoc;
  const feeFa = Math.round(fa * 0.03);
  const platform = feeMo + feeFa;
  const payout = mo + fa;
  const clientTotal = payout + platform;
  return { mo, fa, feeMoIns, feeMoOps, feeMoSal, feeMoMkt, feeMoSoc, feeMo, feeFa, platform, payout, clientTotal };
};

export default function QuoteCreatePage() {
  const [sp] = useSearchParams();
  const convId = sp.get('conversation_id');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [laborUsd, setLaborUsd] = useState('');
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'jobeur') { toast.error('Réservé aux jobeurs'); nav('/'); }
  }, [user, nav]);

  const laborCents = usdToCents(laborUsd);
  const breakdown = useMemo(() => computeLocal(laborCents, lines), [laborCents, lines]);

  const addLine = () => setLines([...lines, { description: '', amount_cents: 0, amount_usd: '', receipt_base64: null }]);
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!convId) return toast.error('Conversation manquante');
    if (!title.trim()) return toast.error('Titre requis');
    if (laborCents <= 0) return toast.error('Main d’œuvre requise (> 0)');
    for (const l of lines) {
      if (!l.description.trim() || (l.amount_cents || 0) <= 0) return toast.error('Chaque frais annexe doit avoir une description et un montant');
    }
    setSaving(true);
    try {
      const r = await api.post('/quotes', {
        conversation_id: convId,
        title,
        description: desc,
        labor_cents: laborCents,
        expenses: lines.map((l) => ({ description: l.description, amount_cents: l.amount_cents, receipt_base64: l.receipt_base64 })),
      });
      toast.success('Devis envoyé au client');
      nav(`/quote/${r.data.quote.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l’envoi');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="quote-create-page">
      <div className="flex items-center gap-2">
        <FileText size={22} className="text-[#1677F2]" />
        <h1 className="text-2xl md:text-3xl font-bold">Émettre un devis</h1>
      </div>
      <SecurityBanner variant="compact" />

      <Card className="p-4 space-y-4">
        <div>
          <Label>Titre du devis</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Réparation fuite salle de bain" data-testid="quote-title" />
        </div>
        <div>
          <Label>Détail du travail</Label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid="quote-desc" />
        </div>

        <Tabs defaultValue="mo">
          <TabsList>
            <TabsTrigger value="mo" data-testid="quote-tab-mo">Main d&apos;œuvre (MO)</TabsTrigger>
            <TabsTrigger value="fa" data-testid="quote-tab-fa">Frais annexes (FA)</TabsTrigger>
          </TabsList>

          <TabsContent value="mo" className="mt-4 space-y-3">
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-[#EAF3FF] p-3 rounded-lg border border-[#C7E2FF]">
              <Info size={14} className="flex-shrink-0 mt-0.5 text-[#0E3F80]" />
              <div>
                <p className="font-medium text-[#0E3F80]">Main d&apos;œuvre (votre rémunération)</p>
                <p>Commission Job Kongo de 10% répartie : 3% assurance, 2% fonctionnement, 3% salaires, 1% marketing, 1% œuvres sociales.</p>
              </div>
            </div>
            <div>
              <Label>Montant MO en $ (USD)</Label>
              <Input type="number" step="0.01" min="0" value={laborUsd} onChange={(e) => setLaborUsd(e.target.value)} placeholder="50.00" data-testid="quote-labor-input" />
            </div>
          </TabsContent>

          <TabsContent value="fa" className="mt-4 space-y-3">
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-[#DFF7EA] p-3 rounded-lg border border-[#1F9D63]/30">
              <Info size={14} className="flex-shrink-0 mt-0.5 text-[#16764A]" />
              <div>
                <p className="font-medium text-[#16764A]">Frais annexes (dépenses réelles)</p>
                <p>Exonérés des 10%. Seulement 3% d&apos;assurance. Justificatif obligatoire (photo de reçu).</p>
              </div>
            </div>
            {lines.map((l, i) => (
              <Card key={i} className="p-3 space-y-2 border-[#E2E8F0]" data-testid={`quote-line-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Description</Label>
                    <Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Ex. Peinture 5L" data-testid={`quote-line-desc-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Montant ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={l.amount_usd}
                      onChange={(e) => updateLine(i, { amount_usd: e.target.value, amount_cents: usdToCents(e.target.value) })}
                      data-testid={`quote-line-amount-${i}`}
                    />
                  </div>
                </div>
                <ImageUpload value={l.receipt_base64} onChange={(v) => updateLine(i, { receipt_base64: v })} label="Ajouter la photo du reçu" testid={`quote-line-receipt-${i}`} />
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => removeLine(i)} className="text-[#CE1021]" data-testid={`quote-line-remove-${i}`}>
                    <Trash2 size={14} className="mr-1" /> Retirer
                  </Button>
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={addLine} data-testid="quote-fa-add-line-button">
              <Plus size={14} className="mr-1" /> Ajouter un frais annexe
            </Button>
          </TabsContent>
        </Tabs>

        {/* Summary */}
        <Card className="bg-[#F7FAFF] border-[#C7E2FF] p-4 mt-2">
          <h3 className="font-semibold mb-2">Récapitulatif</h3>
          <div className="space-y-1 text-sm">
            <Row label="Main d'œuvre (MO)" value={fmtUSD(breakdown.mo)} />
            <Row label="Frais annexes (FA)" value={fmtUSD(breakdown.fa)} />
            <Separator className="my-2" />
            <Row label="Commission 10% (MO)" value={fmtUSD(breakdown.feeMo)} muted />
            <Row label="Assurance 3% (FA)" value={fmtUSD(breakdown.feeFa)} muted />
            <Separator className="my-2" />
            <Row label="👉 Total client" value={fmtUSD(breakdown.clientTotal)} strong testid="quote-total-amount" />
            <Row label="👉 Reçu par le jobeur (MO+FA)" value={fmtUSD(breakdown.payout)} />
            <Row label="👉 Perçu par Job Kongo" value={fmtUSD(breakdown.platform)} />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full mt-4 bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="quote-submit-button">
            {saving ? 'Envoi…' : 'Envoyer le devis au client'}
          </Button>
        </Card>
      </Card>
    </div>
  );
}

const Row = ({ label, value, muted, strong, testid }) => (
  <div className={`flex items-center justify-between ${strong ? 'text-base' : ''}`}>
    <span className={muted ? 'text-slate-600' : 'text-slate-700'}>{label}</span>
    <span className={`font-mono-num ${strong ? 'font-bold text-[#0B1220]' : 'font-semibold text-[#0B1220]'}`} data-testid={testid}>{value}</span>
  </div>
);
